/* @flow */

import React, { Component } from 'react'
import Dropzone from 'react-dropzone'
import ReactCrop from 'react-image-crop'
import { connect } from 'react-redux'
import { compose, withState } from 'recompose'
import {
  setIconFile,
  setIconBlob,
  setIconCrop,
  getIconCrop,
  getIconBlob,
  getIconFile,
} from '../../stores/icons/current'
import { setSelectedExpertOpinionName } from '../../stores/pollBuilderSelectedExpertOpinion'
import ColorButton from '../Controls/ColorButton'
import type { Crop, ImageFile } from '../../types'

import {
  uploadIconInit,
  uploadIconStart,
  getUploadStatus,
  getUploadError,
  getIcon,
} from '../../stores/icons/creation'

type Event = {
  target: {
    value: string,
  },
}

type CreatorState = {
  name: string,
  nameTouched: boolean,
  nameError: boolean,
}

type CreatorProps = {
  width: number,
  height: number,
  uploadStatus: string,
  clickedIconIndex: number,
  file: ?ImageFile,
  blob: ?string,
  crop: Crop,
  onSave: (name: string, blob: string) => void,
  reset: () => void,
  cancel: () => void,
  setClickedIconIndex: () => void,
  setSelectedExpertOpinionName: string => void,
  setIconFile: (Object[]) => void,
  setIconCrop: Crop => void,
  setIconBlob: File => void,
}

function makeInitialState(): CreatorState {
  return {
    name: '',
    nameTouched: false,
    nameError: false,
  }
}

const dropZoneStyles = {
  style: {
    width: '100%',
    height: 200,
    borderWidth: 2,
    borderColor: '#666',
    borderStyle: 'dashed',
    borderRadius: 5,
  },
  activeStyle: {
    borderStyle: 'solid',
    backgroundColor: '#eee',
  },
  rejectStyle: {
    borderStyle: 'solid',
    backgroundColor: '#ffdddd',
  },
}

export class MediaIconCreator extends Component<CreatorProps, CreatorState> {
  dropZone: ?Element

  constructor(props: CreatorProps) {
    super(props)
    this.state = makeInitialState()
  }

  componentDidMount() {
    this.props.reset()
  }

  render() {
    const {
      file,
      crop,
      uploadStatus,
      setIconFile,
      width,
      height,
      cancel,
    } = this.props
    const { name, nameError } = this.state
    const cropArea = crop || {
      width,
      height,
      aspect: width / height,
      x: 0,
      y: 0,
    }

    return (
      <div>
        <div className="ph4 pv2">
          <input
            className="h2 bg-transparent bb dark-gray tl w-100 mb2 b--moon-gray"
            placeholder="Name"
            value={name}
            onBlur={this.touchName}
            onChange={this.setName}
          />

          <div>
            {file && cropArea ? (
              <div>
                <div>
                  <ReactCrop
                    src={file.preview}
                    crop={cropArea}
                    onImageLoaded={crop => this.setCropAndBlob(crop)}
                    onComplete={crop => this.setCropAndBlob(crop)}
                  />
                </div>
              </div>
            ) : (
              <Dropzone
                ref={dropZone => (this.dropZone = dropZone)}
                onDrop={files => setIconFile(files[0])}
                multiple={false}
                accept="image/*"
                {...dropZoneStyles}
              >
                <div style={{ textAlign: 'center', lineHeight: '200px' }}>
                  Drag any image here, or click to select files to upload
                </div>
              </Dropzone>
            )}
          </div>
        </div>

        <div className="bg-light-gray f7 ph4 pv3 w-100 flex items-center justify-center">
          <div className="w-90 tr">
            <ColorButton
              colorClass="gray"
              borderClass="b--gray"
              className="mr2"
              onClick={cancel}
            >
              Cancel
            </ColorButton>

            {uploadStatus === 'initialized' ? (
              <ColorButton
                colorClass="op-brand"
                borderClass="b--op-brand"
                onClick={this.save}
              >
                Create
              </ColorButton>
            ) : uploadStatus === 'uploading' ? (
              <ColorButton
                colorClass="op-brand"
                borderClass="b--op-brand"
                disabled
              >
                Uploading
              </ColorButton>
            ) : (
              <div className="red">An error occured</div>
            )}

            {nameError && <div className="red">Name is required</div>}
          </div>
        </div>
      </div>
    )
  }

  setName = (event: Event) => {
    const name = event.target.value
    this.setState({ name, nameTouched: true })
    this.props.setSelectedExpertOpinionName(name)
  }

  touchName = () => {
    this.setState({ nameTouched: true })
  }

  setCropAndBlob = (newCrop: Crop) => {
    const { crop, file, setIconCrop, setIconBlob, width, height } = this.props
    setIconCrop(newCrop)

    let image = new Image()
    image.src = file && file.preview ? file.preview : ''

    image.onload = () => {
      const canvas = document.createElement('CANVAS')
      // flow checking
      if (!(canvas instanceof HTMLCanvasElement)) {
        return
      }

      const isTheSameCoords = crop
        ? newCrop.x === crop.x && newCrop.y === crop.y
        : false
      const isTheSameDimensions = crop
        ? newCrop.height === crop.height || newCrop.width === crop.width
        : false

      if (!image || !canvas || (isTheSameCoords && isTheSameDimensions)) return

      const imageWidth = image.naturalWidth
      const imageHeight = image.naturalHeight

      const cropX = (newCrop.x / 100) * imageWidth
      const cropY = (newCrop.y / 100) * imageHeight

      const cropWidth = (newCrop.width / 100) * imageWidth
      const cropHeight = (newCrop.height / 100) * imageHeight

      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')

      if (ctx) {
        ctx.drawImage(
          image,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          width,
          height
        )
      }

      canvas.toBlob(blob => {
        setIconBlob(blob)
      })
    }
  }

  getNameValidation = () => {
    const { name, nameTouched } = this.state
    const errors = []

    if (!nameTouched)
      return {
        validationState: null,
        errors,
      }

    // basic not emoty string
    if (name.length <= 0) errors.push('Name is mandatory')

    return {
      validationState: errors.length ? 'error' : 'success',
      errors,
    }
  }

  save = () => {
    const { onSave, blob } = this.props
    const { name } = this.state
    const { validationState: nameValidation } = this.getNameValidation()

    this.setState({
      nameError: nameValidation !== 'success',
    })

    if (nameValidation === 'success' && blob) {
      onSave(name, blob)
    }
  }
}

export default compose(
  connect(
    state => ({
      uploadStatus: getUploadStatus(state),
      uploadError: getUploadError(state),
      icon: getIcon(state),
      file: getIconFile(state),
      crop: getIconCrop(state),
      blob: getIconBlob(state),
    }),
    dispatch => ({
      onSave: (name, blob) => dispatch(uploadIconStart(name, blob)),
      reset: () => dispatch(uploadIconInit()),
      setIconCrop: crop => dispatch(setIconCrop(crop)),
      setIconBlob: blob => dispatch(setIconBlob(blob)),
      setIconFile: file => dispatch(setIconFile(file)),
      setSelectedExpertOpinionName: name =>
        dispatch(setSelectedExpertOpinionName(name)),
    })
  ),
  withState('clickedIconIndex', 'setClickedIconIndex', null)
)(MediaIconCreator)



// WEBPACK FOOTER //
// ./src/components/MediaIconCreator/index.js